import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(JSON.parse(localStorage.getItem('user') || 'null'))
  const token = ref(localStorage.getItem('token') || '')
  const loading = ref(false)
  const error = ref(null)

  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isDoctor = computed(() => user.value?.role === 'doctor')
  const isStaff = computed(() => user.value?.role === 'staff')
  const userDistrict = computed(() => user.value?.district_id)

  const login = async (email, password) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/auth/login', { email, password })
      
      // Handle demo mode response (flat structure)
      if (response.token && response.user) {
        token.value = response.token
        user.value = response.user
      }
      // Handle normal API response (nested in data)
      else if (response.data?.data?.token && response.data?.data?.user) {
        token.value = response.data.data.token
        user.value = response.data.data.user
      }
      // Fallback: data directly on response.data
      else if (response.data?.token && response.data?.user) {
        token.value = response.data.token
        user.value = response.data.user
      } else {
        throw new Error('Invalid response structure')
      }

      localStorage.setItem('token', token.value)
      localStorage.setItem('user', JSON.stringify(user.value))
      return true
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Login failed'
      error.value = errorMsg
      console.error('Login error:', errorMsg)
      return false
    } finally {
      loading.value = false
    }
  }

  const register = async (email, password, fullName) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/auth/register', { email, password, full_name: fullName })
      token.value = response.data.token
      user.value = response.data.user
      localStorage.setItem('token', token.value)
      localStorage.setItem('user', JSON.stringify(user.value))
      return true
    } catch (err) {
      error.value = err.response?.data?.message || 'Registration failed'
      return false
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    token.value = ''
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  const getProfile = async () => {
    try {
      const response = await api.get('/auth/profile')
      user.value = response.data?.data || response.data
      localStorage.setItem('user', JSON.stringify(user.value))
      return user.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to get profile'
    }
  }

  const changePassword = async (currentPassword, newPassword) => {
    loading.value = true
    error.value = null
    try {
      await api.post('/auth/change-password', { 
        old_password: currentPassword,
        new_password: newPassword 
      })
      return true
    } catch (err) {
      error.value = err.response?.data?.message || 'Password change failed'
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isDoctor,
    isStaff,
    userDistrict,
    login,
    register,
    logout,
    getProfile,
    changePassword,
  }
})
